import { cn } from '@invent/wl-ui-kit-next';
import {
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@invent/wl-ui-kit-next/ui';
import { Check, ChevronDown, CircleHelp, LoaderCircle, Search, X } from 'lucide-react';
import * as React from 'react';

import {
  CompactFilters,
  FilterBox,
  NAMES_ONLY,
  QuickChipsScope,
  useCondensed,
  useQuickChips
} from './condense';

/**
 * A list's filters as one line (the two-portals design, §5): one submitted search box, a chip per
 * filter, "Clear" once anything narrows the list, and the count at the right.
 *
 * **The URL is the state, and none of it is here.** Every chip is controlled by the page, which
 * reads its value out of the address and writes a change back into it, with the rules the lists
 * already keep: a value no control knows is still sent and drawn as spelled, and a result is drawn
 * only under the query it answers. The bar only lays the controls out.
 *
 * **The box is submitted, never sent per keystroke**: each list's search is a server search over
 * everything held. What it matches is said behind a "?" beside the box (`help`, "About this
 * search"), in survey-api's rule — never as lines under the bar, where it took three or four rows
 * from the table on every visit (SR-439).
 *
 * Three kinds of chip:
 * - `FilterChip` — a chip opening a popover that holds a group of boxes, of radios, or a field;
 *   the popover is a dialog named as the filter, and its group keeps the filter's name, so a
 *   control inside is found as it was before it moved into a popover;
 * - `SelectChip` — one value from a vocabulary, the kit's select drawn as a chip; the combobox is
 *   named as the filter and reads the position chosen;
 * - `ToggleChip` — one switch, a checkbox named as the filter.
 *
 * **While the list's next read is out, the count says "Searching…"** (`busy`) — the last answer's
 * count would be a number about a query no longer asked. A list that passes `busy` at all keeps
 * its count in one `status` region, mounted for good and only its words swapped, so a screen
 * reader hears "Searching…" and then the count it lands on; `data-list-reading` is where a spec
 * finds the words. The table beside it keeps its rows and draws its own bar (`DataTable`'s
 * `busy`).
 *
 * `data-filter-bar` on the bar is what a spec finds a chip inside, apart from a table's controls
 * of the same name.
 */
export function FilterBar({
  search,
  children,
  count,
  onClear,
  help,
  busy,
  end
}: {
  search?: {
    label: string;
    placeholder: string;
    draft: string;
    onDraft: (value: string) => void;
    onSubmit: () => void;
    /** A narrower box, where the line holds many controls. */
    narrow?: boolean;
    /** A work page's box, filtering the list it heads: drawn as `FilterBox`, sent by *Filter*. */
    filters?: boolean;
  };
  children?: React.ReactNode;
  /** What the list holds under the query — "812 found". */
  count?: React.ReactNode;
  /** Present once a search or a filter narrows the list. */
  onClear?: () => void;
  /** What the list holds and its search matches — behind "About this search". */
  help?: React.ReactNode;
  /** The list's next read is out — said in place of the count. A list that passes it keeps
   *  the count in a live region. */
  busy?: boolean;
  /** Drawn after the count, at the line's end — a work page's actions on the whole list. */
  end?: React.ReactNode;
}) {
  return (
    <div data-filter-bar className="grid gap-2">
      <div className="flex flex-wrap items-center gap-2">
        {search && (
          <form
            role="search"
            onSubmit={(event) => {
              event.preventDefault();
              search.onSubmit();
            }}
            className={cn('flex w-full min-w-0 shrink-0 gap-1.5', search.narrow ? 'sm:w-72' : 'sm:w-96')}
          >
            {search.filters ? (
              <FilterBox
                label={search.label}
                placeholder={search.placeholder}
                value={search.draft}
                onChange={search.onDraft}
                className="flex-1"
              />
            ) : (
              <Input
                className="min-w-0 flex-1"
                type="search"
                value={search.draft}
                onChange={(event) => search.onDraft(event.target.value)}
                placeholder={search.placeholder}
                aria-label={search.label}
              />
            )}
            <Button type="submit" size="sm">
              {!search.filters && <Search className="size-4" aria-hidden />}
              {search.filters ? 'Filter' : 'Search'}
            </Button>
          </form>
        )}
        {help && <AboutSearch help={help} />}
        {children}
        {onClear && (
          <Button type="button" variant="ghost" size="sm" onClick={onClear} className="gap-1">
            <X className="size-3.5" aria-hidden />
            Clear
          </Button>
        )}
        {busy !== undefined ? (
          // Mounted empty and kept: a live region is heard when its words change, not when it
          // arrives with them.
          <span role="status" className={COUNT}>
            {busy ? (
              <span data-list-reading className="inline-flex items-center gap-1.5">
                <LoaderCircle
                  className="size-3.5 animate-spin text-[var(--color-sapphire)] motion-reduce:animate-none"
                  aria-hidden
                />
                Searching…
              </span>
            ) : (
              count
            )}
          </span>
        ) : (
          count !== undefined && <span className={COUNT}>{count}</span>
        )}
        {end}
      </div>
    </div>
  );
}

/** Which quick filter's control is open, and whether the toolbar folds them — held by the page,
 *  so a chip of the applied filters' row opens its control (`openQuick`). */
export interface IWorkToolbar {
  open: Nullable<string>;
  setOpen: (label: Nullable<string>) => void;
  folded: boolean;
  setFolded: (folded: boolean) => void;
  filtersOpen: boolean;
  setFiltersOpen: (open: boolean) => void;
  /** Opens a quick filter's control by its name — inside the folded *Filters* where it is. */
  openQuick: (label: string) => true;
}

export function useWorkToolbar(): IWorkToolbar {
  const [open, setOpen] = React.useState<Nullable<string>>(null);
  const [folded, setFolded] = React.useState(false);
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  // The control a folded *Filters* opens after it, kept so a newer press, or leaving the page,
  // cancels the older one.
  const pending = React.useRef<number>();
  React.useEffect(() => () => window.clearTimeout(pending.current), []);
  return {
    open,
    setOpen,
    folded,
    setFolded,
    filtersOpen,
    setFiltersOpen,
    openQuick: (label) => {
      if (!folded) {
        window.clearTimeout(pending.current);
        setOpen(label);
        return true;
      }
      // Folded: *Filters* opens first and takes the focus; the control opens inside it after, or
      // that first focus would read as a press outside it and shut it.
      setFiltersOpen(true);
      window.clearTimeout(pending.current);
      pending.current = window.setTimeout(() => setOpen(label), FOLDED_OPEN_MS);
      return true;
    }
  };
}

/** How long a folded *Filters* is given to open before the control inside it opens. */
const FOLDED_OPEN_MS = 60;

/** The room a search box keeps before the quick filters fold — 16rem. */
const SEARCH_ROOM = 256;
const ROW_GAP = 8;

/**
 * A work page's toolbar row (SR-514): the search box, the quick filters by name alone, and the
 * page's own buttons pinned right — one line that never wraps on a desktop, whatever is chosen;
 * below 1024 px, where the page is not designed for and must not be wider than the screen, it wraps. Where the row
 * cannot hold the quick filters beside a box of 16rem, they fold into one *Filters (n)* — the compact
 * bar's — measured on a hidden copy, so what is drawn never changes what fits. The values chosen
 * are the applied filters' row under it (`CompactChipLine`). A page with no search (My tasks)
 * passes none, and its quick filters keep the box's room. `lead` and `after` hold what never folds:
 * a scope the rows are read in, which says its value (DD calls' *Whose*), and *Advanced…*.
 * `status` is the list's read said to a screen reader — "Searching…" while it is out, then the count
 * it lands on — in one `status` region mounted for good; the eye has the rows' veil and the pager.
 */
export function WorkToolbar({
  toolbar,
  search,
  quick,
  quickCount,
  end,
  lead,
  after,
  menu,
  status
}: {
  toolbar: IWorkToolbar;
  /** The list's search box; none where the page has no search. */
  search?: React.ReactNode;
  quick: React.ReactNode;
  /** The quick filters set — the folded button's count. */
  quickCount: number;
  end?: React.ReactNode;
  /** Drawn after the box, never folded nor by name alone — a scope that says its value. */
  lead?: React.ReactNode;
  /** Drawn after the quick filters, or *Filters*, never folded — *Advanced…*. */
  after?: React.ReactNode;
  /** The page's one *Filters* menu (SR-530) — field search, *Advanced…*, *Clear all* — drawn after the
   *  quick filters; folded, they go inside it (`quick` handed to it), not into a *Filters* of their
   *  own. Without it they fold into the kit's `CompactFilters`. */
  menu?: (quick: React.ReactNode) => React.ReactNode;
  /** The list's read: out, or the count it answered ("12 found"). */
  status?: { reading: boolean; count?: string };
}) {
  const row = React.useRef<HTMLDivElement>(null);
  const whole = React.useRef<HTMLDivElement>(null);
  const tail = React.useRef<HTMLDivElement>(null);
  const before = React.useRef<HTMLDivElement>(null);
  const behind = React.useRef<HTMLDivElement>(null);
  const { setFolded } = toolbar;
  const searched = search !== undefined && search !== null;
  const led = lead !== undefined && lead !== null && lead !== false;
  const trailed = (after !== undefined && after !== null && after !== false) || menu !== undefined;
  // No quick filters (Lists, Reports): nothing folds, so no empty *Filters* is drawn on a short row.
  const quicked = quick !== undefined && quick !== null && quick !== false;
  React.useLayoutEffect(() => {
    const box = row.current;
    const copy = whole.current;
    const right = tail.current;
    if (!box || !copy || !right) return;
    const room = searched ? SEARCH_ROOM + ROW_GAP : 0;
    const kept = [before.current, behind.current].flatMap((element) => element ?? []);
    const measure = () => {
      const fixed = kept.reduce((sum, element) => sum + element.offsetWidth + ROW_GAP, 0);
      setFolded(room + fixed + copy.offsetWidth + right.offsetWidth + 2 * ROW_GAP > box.clientWidth);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    observer.observe(copy);
    observer.observe(right);
    for (const element of kept) observer.observe(element);
    return () => observer.disconnect();
  }, [setFolded, searched, led, trailed]);
  const scope = { open: toolbar.open, setOpen: toolbar.setOpen };
  return (
    <div ref={row} data-filter-bar className="relative flex min-w-0 flex-wrap items-center gap-2 lg:flex-nowrap">
      <div
        ref={whole}
        aria-hidden
        className="pointer-events-none invisible absolute top-0 left-0 flex w-max items-center gap-2"
      >
        <QuickChipsScope value={NAMES_ONLY}>{quick}</QuickChipsScope>
      </div>
      {/* The box's own room, 24rem, given up before anything else is. */}
      {searched && <div className="flex min-w-48 shrink basis-96">{search}</div>}
      {led && (
        <div ref={before} className="flex shrink-0 items-center gap-2">
          {lead}
        </div>
      )}
      <QuickChipsScope value={scope}>
        {!quicked ? null : toolbar.folded ? (
          !menu && (
            <CompactFilters count={quickCount} open={toolbar.filtersOpen} onOpenChange={toolbar.setFiltersOpen}>
              {quick}
            </CompactFilters>
          )
        ) : (
          <div className="flex shrink-0 items-center gap-2">{quick}</div>
        )}
        {trailed && (
          <div ref={behind} className="flex shrink-0 items-center gap-2">
            {menu?.(quicked && toolbar.folded ? quick : null)}
            {after}
          </div>
        )}
      </QuickChipsScope>
      <span className="min-w-0 flex-1" />
      <div ref={tail} className="flex shrink-0 items-center gap-2">
        {end}
      </div>
      {status && (
        <span role="status" className="sr-only">
          {status.reading ? <span data-list-reading="">Searching…</span> : (status.count ?? '')}
        </span>
      )}
    </div>
  );
}

/** What a list's search matches and what the list holds, behind a "?" — read when wanted. */
function AboutSearch({ help }: { help: React.ReactNode }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label="About this search"
          className="h-8 w-8 p-0 text-muted-foreground"
        >
          <CircleHelp className="size-4" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        aria-label="About this search"
        data-search-help
        className="max-w-[min(32rem,90vw)] text-xs leading-relaxed text-muted-foreground"
      >
        {help}
      </PopoverContent>
    </Popover>
  );
}

const COUNT = 'ml-auto text-xs whitespace-nowrap text-muted-foreground tabular-nums';

const CHIP =
  'inline-flex h-8 max-w-full items-center gap-1.5 rounded-full border px-3 text-[0.8125rem] whitespace-nowrap transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring';

function chipTone(active: boolean): string {
  return active
    ? 'border-[color-mix(in_srgb,var(--color-teal)_45%,white)] bg-[color-mix(in_srgb,var(--color-teal)_9%,white)] text-[var(--color-royal-blue)]'
    : 'border-border bg-white text-[var(--veritant-royal-70)] hover:border-[var(--veritant-royal-55)]';
}

/**
 * A filter behind a chip. The chip says the filter's name and, once set, what it is set to; the
 * popover holds the controls, and stays open while they are changed, so a group of boxes can be
 * ticked in one visit.
 */
export function FilterChip({
  label,
  summary,
  children,
  className
}: {
  label: string;
  /** What the filter is set to, or nothing while it narrows nothing — so a value the URL names
   *  empty is said by `asSpelled`, or the chip would read as no filter. */
  summary?: Nullable<string>;
  children: React.ReactNode;
  className?: string;
}) {
  const active = summary !== undefined && summary !== null && summary !== '';
  const quick = useQuickChips();
  return (
    <Popover {...openOf(quick, label)}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={active ? `${label}: ${summary}` : label}
          className={cn(CHIP, chipTone(active))}
        >
          <span className={active ? 'text-[var(--veritant-royal-70)]' : undefined}>{label}</span>
          {quick ? <ActiveDot active={active} /> : active && <span className="truncate font-medium">{summary}</span>}
          <ChevronDown className="size-3.5 shrink-0 opacity-60" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        aria-label={label}
        className={cn('grid w-auto max-w-[min(28rem,90vw)] gap-3 p-3 text-[0.8125rem]', className)}
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}

/**
 * A value the URL names, as a chip says it: as spelled, and `''` — `?registry=`, a link cut short —
 * as "Blank", since a chip reading nothing would claim no filter is asked (the SEC ADV register's
 * `Blank` is the precedent). For a chip's summary and its positions; the value itself is still the
 * query, sent as it is for survey-api to judge.
 */
export function asSpelled(value: string): string {
  return value === '' ? 'Blank' : value;
}

/**
 * One value from a vocabulary, as a chip: the first position narrows nothing, and a value the URL
 * names that is not among the options is a position of its own, spelled as the URL spells it — so
 * the query asked is the one the chip shows.
 *
 * **Every value goes to the kit's select under a prefix** (`positionOf`): the select keeps `''` for
 * "nothing chosen" and draws its placeholder for it, so an empty value handed over as itself was a
 * position of no words, chosen under a trigger reading nothing. Prefixed, `''` is a position like
 * any other, and so is a value spelled as `ANY`.
 */
export function SelectChip({
  label,
  any,
  value,
  options,
  onChoose,
  disabled
}: {
  label: string;
  /** The position that narrows nothing. */
  any: string;
  value: Nullable<string>;
  options: readonly { value: string; label: string }[];
  onChoose: (value: Nullable<string>) => void;
  disabled?: boolean;
}) {
  const known = value === null || options.some((option) => option.value === value);
  const positions = known ? options : [...options, { value, label: asSpelled(value) }];
  // In a compact bar a chip that narrows nothing says its name alone, as a `FilterChip` does; a
  // quick filter drawn by name never says its value.
  const { condensed } = useCondensed();
  const quick = useQuickChips();
  const bare = quick !== null || (condensed && value === null);
  return (
    <span className={cn(CHIP, chipTone(value !== null), 'gap-0 pr-1 pl-3')}>
      <span aria-hidden className="mr-1 text-[var(--veritant-royal-70)]">
        {label}
      </span>
      {quick && <ActiveDot active={value !== null} />}
      <Select
        value={positionOf(value)}
        onValueChange={(position) => onChoose(valueAt(position))}
        disabled={disabled}
        {...openOf(quick, label)}
      >
        <SelectTrigger
          aria-label={label}
          className="!h-7 w-auto gap-1 border-0 bg-transparent px-1.5 font-medium text-[var(--color-royal-blue)] shadow-none"
        >
          <SelectValue>{bare ? null : undefined}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ANY}>{any}</SelectItem>
          {positions.map((option) => (
            <SelectItem key={option.value} value={positionOf(option.value)}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </span>
  );
}

/** A filter that is on or off, as a chip that is a checkbox. */
export function ToggleChip({
  label,
  checked,
  onChange,
  disabled
}: {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(CHIP, chipTone(checked), 'disabled:cursor-not-allowed disabled:opacity-50')}
    >
      {checked && <Check className="size-3.5" aria-hidden />}
      {label}
    </button>
  );
}

/** A quick filter's mark while it narrows — its room kept while it does not, so a chip never
 *  moves what follows it. */
function ActiveDot({ active }: { active: boolean }) {
  return (
    <span
      aria-hidden
      className={cn('size-1.5 shrink-0 rounded-full', active ? 'bg-[var(--color-teal)]' : 'bg-transparent')}
    />
  );
}

/** A quick filter's control open and shut by its scope, where the scope holds which is open. */
function openOf(
  quick: ReturnType<typeof useQuickChips>,
  label: string
): { open?: boolean; onOpenChange?: (open: boolean) => void } {
  if (!quick?.setOpen) return {};
  const setOpen = quick.setOpen;
  return { open: quick.open === label, onOpenChange: (open) => setOpen(open ? label : null) };
}

/** The select position that narrows nothing. */
const ANY = '__any__';

/** The prefix a value's position carries in the kit's select, which `ANY` does not start with. */
const VALUE = '=';

/** A value's position in the kit's select — `ANY` for none. */
function positionOf(value: Nullable<string>): string {
  return value === null ? ANY : `${VALUE}${value}`;
}

/** The value a position stands for — `positionOf` read back. */
function valueAt(position: string): Nullable<string> {
  return position.startsWith(VALUE) ? position.slice(VALUE.length) : null;
}
